import { useRef, useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

export const LENS_MAP_IMAGE_TYPES = [
    "Brain Map",
    "Frequency Map",
    "Standard Map",
];

const LENS_MAP_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

export function parseLensNfbMapImages(rawValue) {
    if (!rawValue) return [];
    if (Array.isArray(rawValue)) return rawValue;
    if (typeof rawValue === "string") {
        try {
            const parsed = JSON.parse(rawValue);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
}

function createLensMapImageId() {
    return `lens-map-image-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function readImageFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("The image could not be read."));
        reader.readAsDataURL(file);
    });
}

function LensMapImagesSection({ value = [], onChange, disabled = false }) {
    const fileInputRef = useRef(null);
    const [previewImage, setPreviewImage] = useState(null);
    const images = parseLensNfbMapImages(value);

    const updateImages = (nextImages) => {
        onChange(nextImages);
    };

    const handleFilesSelected = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = "";

        const nextImages = [...images];

        for (const file of files) {
            if (!file.type?.startsWith("image/")) {
                window.alert(`${file.name} is not a supported image file.`);
                continue;
            }

            if (file.size > LENS_MAP_IMAGE_MAX_BYTES) {
                window.alert(
                    `${file.name} is too large. Please upload images smaller than 5 MB.`
                );
                continue;
            }

            try {
                const dataUrl = await readImageFileAsDataUrl(file);
                nextImages.push({
                    id: createLensMapImageId(),
                    fileName: file.name,
                    dataUrl,
                    mapType: LENS_MAP_IMAGE_TYPES[0],
                    notes: "",
                });
            } catch (error) {
                console.error("Failed to upload map image", error);
                window.alert("The image could not be uploaded. Please try again.");
            }
        }

        updateImages(nextImages);
    };

    const updateImageField = (imageId, fieldName, fieldValue) => {
        updateImages(
            images.map((imageItem) =>
                imageItem.id === imageId
                    ? { ...imageItem, [fieldName]: fieldValue }
                    : imageItem
            )
        );
    };

    const removeImage = (imageId) => {
        updateImages(images.filter((imageItem) => imageItem.id !== imageId));
    };

    return (
        <>
        <Accordion defaultExpanded sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Map Images</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload one or more map images.
                </Typography>

                {!disabled && (
                    <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            hidden
                            onChange={handleFilesSelected}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            Upload Image
                        </Button>
                    </>
                )}

                {images.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No map images uploaded yet.
                    </Typography>
                ) : (
                    <Stack spacing={2}>
                        {images.map((imageItem, index) => (
                            <Box
                                key={imageItem.id}
                                sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    p: 2,
                                    bgcolor: "grey.50",
                                }}
                            >
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                    spacing={2}
                                    sx={{ mb: 2 }}
                                >
                                    <Typography fontWeight={600}>
                                        Image {index + 1}
                                    </Typography>
                                    {!disabled && (
                                        <Button
                                            color="error"
                                            size="small"
                                            variant="outlined"
                                            onClick={() => removeImage(imageItem.id)}
                                        >
                                            Remove
                                        </Button>
                                    )}
                                </Stack>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 2, wordBreak: "break-word" }}
                                >
                                    {imageItem.fileName || "Uploaded image"}
                                </Typography>

                                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                    <InputLabel id={`map-type-label-${imageItem.id}`}>
                                        Map Type
                                    </InputLabel>
                                    <Select
                                        labelId={`map-type-label-${imageItem.id}`}
                                        label="Map Type"
                                        value={imageItem.mapType || LENS_MAP_IMAGE_TYPES[0]}
                                        disabled={disabled}
                                        onChange={(e) =>
                                            updateImageField(
                                                imageItem.id,
                                                "mapType",
                                                e.target.value
                                            )
                                        }
                                    >
                                        {LENS_MAP_IMAGE_TYPES.map((mapType) => (
                                            <MenuItem key={mapType} value={mapType}>
                                                {mapType}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <Box
                                    component="img"
                                    src={imageItem.dataUrl || ""}
                                    alt={imageItem.fileName || "Map image preview"}
                                    onClick={() =>
                                        setPreviewImage({
                                            dataUrl: imageItem.dataUrl,
                                            fileName: imageItem.fileName,
                                            mapType: imageItem.mapType,
                                        })
                                    }
                                    sx={{
                                        display: "block",
                                        maxWidth: "100%",
                                        maxHeight: 240,
                                        objectFit: "contain",
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 1,
                                        bgcolor: "background.paper",
                                        mb: 1,
                                        cursor: "pointer",
                                    }}
                                />

                                <Button
                                    size="small"
                                    variant="text"
                                    sx={{ mb: 2, px: 0 }}
                                    onClick={() =>
                                        setPreviewImage({
                                            dataUrl: imageItem.dataUrl,
                                            fileName: imageItem.fileName,
                                            mapType: imageItem.mapType,
                                        })
                                    }
                                >
                                    View Full Size
                                </Button>

                                <TextField
                                    label="Additional Information"
                                    value={imageItem.notes || ""}
                                    onChange={(e) =>
                                        updateImageField(
                                            imageItem.id,
                                            "notes",
                                            e.target.value
                                        )
                                    }
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    disabled={disabled}
                                    placeholder="Enter any additional information about this image"
                                />
                            </Box>
                        ))}
                    </Stack>
                )}
            </AccordionDetails>
        </Accordion>

        <Dialog
            open={!!previewImage}
            onClose={() => setPreviewImage(null)}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle sx={{ pr: 6 }}>
                {previewImage?.fileName || "Map Image"}
                {previewImage?.mapType ? ` (${previewImage.mapType})` : ""}
                <IconButton
                    aria-label="Close"
                    onClick={() => setPreviewImage(null)}
                    sx={{ position: "absolute", right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ textAlign: "center" }}>
                <Box
                    component="img"
                    src={previewImage?.dataUrl || ""}
                    alt={previewImage?.fileName || "Map image preview"}
                    sx={{
                        maxWidth: "100%",
                        maxHeight: "80vh",
                        objectFit: "contain",
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setPreviewImage(null)}>Close</Button>
            </DialogActions>
        </Dialog>
        </>
    );
}

export default LensMapImagesSection;
